<script setup lang="ts">
import { computed, mergeProps } from 'vue';
import { has } from 'lodash';

interface TooltipProps {
  position?: 'top' | 'bottom' | 'right' | 'left';
  disabled?: boolean;
  offset?: number;
  showPointer?: boolean;
}

interface TooltipSlots {
  tooltip(): any;
  target(props: { props: Record<string, any> }): any;
}

const props = withDefaults(defineProps<TooltipProps>(), {
  position: 'top',
  offset: 10,
});

defineOptions({
  inheritAttrs: false,
});

const slots = defineSlots<TooltipSlots>();

const positionMap = {
  top: {
    location: 'top',
    origin: 'bottom',
  },
  bottom: {
    location: 'bottom',
    origin: 'top',
  },
  right: {
    location: 'right',
    origin: 'left',
  },
  left: {
    location: 'left',
    origin: 'right',
  },
} as const;

const vPosition = computed(() => {
  return positionMap[props.position];
});

const offsetString = computed(() => `${props.offset}px`);
</script>

<template>
  <v-tooltip
    :location="vPosition.location"
    :origin="vPosition.origin"
    :disabled="disabled"
    :offset="
      offset - 1 // 1px overlapping to prevent inaccuracies
    "
  >
    <div class="text-pre-line">
      <slot name="tooltip" />
    </div>
    <template v-if="has(slots, 'target')" #activator="{ isActive, props: templateProps }">
      <slot
        name="target"
        :props="
          mergeProps(templateProps, $attrs, {
            class: ['target', ...[showPointer && isActive ? `${position}-arrow` : '']],
          })
        "
      />
    </template>
  </v-tooltip>
</template>

<style scoped lang="scss">
@use '@/styles/variables';
/* stylelint-disable selector-pseudo-class-no-unknown */
:slotted(.target) {
  --offset: v-bind(offsetString);
  $arrow-size: 8;
  $arrow-offset: calc($arrow-size / 2);
  position: relative;
  &.top-arrow,
  &.left-arrow,
  &.bottom-arrow,
  &.right-arrow {
    // overflow: inherit !important; // that's important to make the pseudo-element visible outside the main container
    z-index: 20;
    &::before {
      content: '';
      position: absolute;
      width: #{$arrow-size}px;
      height: #{$arrow-size}px;
      z-index: 21;
      bottom: 0;
      background-color: rgb(var(--v-theme-primary));
      transform-origin: center;
      transform: rotate(45deg);
      border-radius: 1px;
    }
  }
  &.top-arrow {
    &::before {
      left: calc(50% - #{$arrow-offset}px);
      top: calc((#{$arrow-offset}px + var(--offset)) * -1);
    }
  }
  &.bottom-arrow {
    &::before {
      left: calc(50% - #{$arrow-offset}px);
      bottom: calc((#{$arrow-offset}px + var(--offset)) * -1);
    }
  }
  &.right-arrow {
    &::before {
      right: calc((#{$arrow-offset}px + var(--offset)) * -1);
      top: calc(50% - #{$arrow-offset}px);
    }
  }
  &.left-arrow {
    &::before {
      left: calc((#{$arrow-offset}px + var(--offset)) * -1);
      top: calc(50% - #{$arrow-offset}px);
    }
  }
}
</style>
